"use client";
import Link from "next/link";
import { signUpSchema } from "@/utils/validation/SignupSchema";
import { CheckboxField, InputField, PasswordField } from "@/components/ui";
import { Formik, Form, ErrorMessage } from "formik";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Loader } from "@/components/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "@/app/CustomToastProvider";
import { getReferralCodeDetail } from "@/lib/features/auth/authSlice";
import { useAppDispatch } from "@/lib/hooks";
import { Check } from "react-bootstrap-icons";
import { SIGNUP_EVENT, trackEvent } from "@/utils/gtm";
import { Turnstile, type TurnstileHandle } from "@/components/ui";
import { postApi } from "@/utils/http";


interface SignUpFormValues {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirmPassword: string;
  registration_via: string;
  agree: boolean;
  referral_code: string;
}
interface SignUpProps {
  onSuccess?: () => any;
  onLoginClick?: () => any;
}
const SignUpForm = (props: SignUpProps) => {
  const dispatch = useAppDispatch();
  const query: any = useSearchParams();
  const currentUrl = usePathname();
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileRef = useRef<TurnstileHandle>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReferral, setIsReferral] = useState<boolean>(false);
  const router = useRouter();
  const isCaptchaDisabled = process.env.NEXT_PUBLIC_X_ENV?.toLowerCase() === "dev";
  const initialValues: SignUpFormValues = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    registration_via: "web",
    agree: true,
    referral_code: "",
  };
  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    turnstileRef.current?.reset();
  }, []);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    resetTurnstile();
    showToast("CAPTCHA verification failed. Please try again.", "danger");
  }, [resetTurnstile]);

  const handleTurnstileExpire = useCallback(() => {
    resetTurnstile();
    showToast("CAPTCHA expired. Please verify again.", "danger");
  }, [resetTurnstile]);
  

  const onSubmit = async (data: SignUpFormValues) => {
    if (!isCaptchaDisabled && !turnstileToken) {
      showToast('Please complete the CAPTCHA','danger');
      return;
    }
    setIsLoading(true);
    try {
      const result = await postApi({
        url: "v1/auth/user/signup",
        authOptional: true,
        payload: {
          first_name: data.first_name,
          last_name: data.last_name,
          password: data.password,
          email: data.email,
          registration_via: data.registration_via,
          referral_code: data.referral_code,
          ...(!isCaptchaDisabled && { turnstile_challenge_token: turnstileToken }),
        },
      });

      if (result?.status !== 200) {
        showToast(result?.message || "Unable to create account. Please try again.", "danger");
        setIsLoading(false);
        resetTurnstile();
      } else {
        setIsLoading(false);
        props.onSuccess && props.onSuccess();
        trackEvent(SIGNUP_EVENT, {
          method:"email",
          email: data?.email,
        });
        resetTurnstile();
        showToast(result?.message || "Account created. Please verify your email before logging in.", "success");
        router.push(`/verify-email?email=${encodeURIComponent(data.email)}`);
      }
    } catch (error) {
      setIsLoading(false);
      resetTurnstile();
      console.error("Error during signup:", error);
      showToast("There is some error while processing this request. Please try again after some time.", "danger");
    }
  };

  useEffect(() => {
    if (!turnstileToken) return;

    const resetTimeout = setTimeout(() => {
      resetTurnstile();
      showToast("CAPTCHA expired. Please verify again.", "danger");
    }, 120000);

    return () => clearTimeout(resetTimeout);
  }, [turnstileToken, resetTurnstile]);

  return (
    <>
      {isLoading && <Loader show={isLoading} />}
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={signUpSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => {
          useEffect(() => {
            const fetchTokenDetails = async () => {
              const referral_code = query.get("referral_code");
              if (referral_code) {
                const { payload } = await dispatch(
                  getReferralCodeDetail({
                    url: `v1/auth/user/get-referral-code-details`,
                    payload: { referral_code },
                  })
                );
                const { status, data } = payload;
                if (status === 200) {
                  const { referral_code, email } = data;
                  setFieldValue("referral_code", referral_code || "");
                  setIsReferral(true);
                }
              }
            };
            fetchTokenDetails();
          }, [query, dispatch, setFieldValue]);

          return (
            <Form className="row y-gap-20">
              {/* {JSON.stringify(values)} */}
              <div className="col-12">
                {currentUrl == "/signup" && (
                  <h1 className="text-22 fw-500">Welcome back</h1>
                )}
                <p className="mt-10">
                  Already have an account yet?{" "}
                  {currentUrl == "/signup" ? (
                    <Link href="/login" className="text-blue-1">
                      Log in
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => props.onLoginClick && props.onLoginClick()}
                      className="text-blue-1"
                    >
                      Log in
                    </button>
                  )}
                </p>
              </div>
              <div className="col-12 col-sm-6">
                <InputField name="first_name" label="First Name" />
              </div>
              <div className="col-12 col-sm-6">
                <InputField name="last_name" label="Last Name" />
              </div>
              <div className="col-12">
                <InputField name="email" label="Email" type="email" />
              </div>
              <div className="col-12">
                <PasswordField name="password" label="Password" />
              </div>
              <div className="col-12">
                <PasswordField
                  name="confirmPassword"
                  label="Confirm Password"
                />
              </div>
              {isReferral && (
                <div className="col-12">
                  <p className="text-15 mt-10 text-green-2 fw-500">
                    <Check size={28} />
                    Referral code {values?.referral_code} applied
                  </p>
                </div>
              )}
              {/* <div className="col-12">
                <div className="d-flex position-relative">
                  <div className="form-checkbox mt-5">
                    <CheckboxField
                      name="referral"
                      id="referral"
                      showError={false}
                      onValueChange={(e) => {
                        setIsReferral(e);
                      }}
                    />
                  </div>
                  <div className="text-15 lh-15 text-light-1 ml-10">
                    Do you have referral code?
                  </div>
                </div>
                <div className="position-relative">
                  <ErrorMessage
                    name={"referral"}
                    component={"div"}
                    className="error-message-1"
                  />
                </div>
              </div> */}
              {!isCaptchaDisabled && (
                <div className="col-12">
                  <Turnstile
                    ref={turnstileRef}
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                  />
                </div>
              )}

              <div className="col-12">
                <div className="d-flex position-relative">
                  <div className="form-checkbox mt-5">
                    <CheckboxField name="agree" id="agree" showError={false} />
                  </div>
                  <div className="text-15 lh-15 text-light-1 ml-10">
                    By creating an account, you agree to our{" "}
                    <Link
                      href={"/terms-and-conditions"}
                      className="text-blue-1"
                    >
                      Terms & Conditions
                    </Link>{" "}
                    and{" "}
                    <Link href={"/privacy-policy"} className="text-blue-1">
                      Privacy Policy
                    </Link>
                    .
                  </div>
                </div>
                <div className="position-relative">
                  <ErrorMessage
                    name={"agree"}
                    component={"div"}
                    className="error-message-1"
                  />
                </div>
              </div>
              <div className="col-12">
                <button
                  type="submit"
                  className="button py-20 -theme-blue-color bg-theme-brown-color text-white w-100"
                >
                  Sign Up
                  {/* <div className="icon-arrow-top-right ml-15" /> */}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default SignUpForm;
